import { useEffect, useState } from 'react';
import { FormModal, FormField, FormRow, Input, Select } from '../../ui';
import type { BatchConfig, BatchRead, ReclusterRequest } from '../../../types/clustering';

interface ReclusterModalProps {
  isOpen: boolean;
  batch: BatchRead;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (settings: ReclusterRequest) => void;
}

function num(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function ReclusterModal({
  isOpen,
  batch,
  isSubmitting,
  onClose,
  onConfirm,
}: ReclusterModalProps) {
  const config = batch.config as Partial<BatchConfig>;
  const configThreshold = config.threshold;
  const configTargetDomain = config.target_domain;
  const [threshold, setThreshold] = useState(2);
  const [targetDomain, setTargetDomain] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setThreshold(Math.max(2, num(configThreshold, 2)));
    setTargetDomain(configTargetDomain ?? '');
  }, [isOpen, batch.id, configTargetDomain, configThreshold]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onConfirm({
      threshold: Math.min(10, Math.max(2, Math.round(threshold))),
      target_domain: targetDomain.trim() || null,
    });
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Re-cluster with same SERPs"
      size="medium"
      onSubmit={handleSubmit}
      submitLabel="Re-cluster for 0 credits"
      isSubmitting={isSubmitting}
    >
      <p>
        Reuses the stored SERP data from this completed batch. No crawler,
        DataForSEO, or billing calls are made.
      </p>

      <FormRow>
        <FormField
          label="SERP overlap threshold (2–10)"
          htmlFor="recluster-threshold"
          helpText="Raw shared top-10 organic URLs required to link keywords. Higher values = stricter clusters."
        >
          <Select
            id="recluster-threshold"
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            fullWidth
          >
            {Array.from({ length: 9 }, (_, i) => i + 2).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Select>
        </FormField>

        <FormField label="Target domain (optional)" htmlFor="recluster-domain">
          <Input
            id="recluster-domain"
            type="text"
            placeholder="example.com"
            value={targetDomain}
            onChange={(e) => setTargetDomain(e.target.value)}
            fullWidth
          />
        </FormField>
      </FormRow>
    </FormModal>
  );
}